import type { Component, VNode } from 'vue';
import { useSlots } from 'vue';
import { v4 as uuidv4 } from 'uuid';

type Slots = ReturnType<typeof useSlots>;

/**
 * Check if a named slot has actual content.
 * Handles empty slots, text nodes, and component/element nodes.
 * Could be REMOVED after vue3 migration
 */
export const hasSlotContent = (slots: Slots, name: string): boolean => {
    const slotFn = slots[name];
    if (!slotFn) return false;

    const vnodes = slotFn();
    if (!vnodes) return false;

    const nodes = Array.isArray(vnodes) ? vnodes : [vnodes];

    return nodes.some((vnode: VNode) => {
        if (!vnode) return false;
        // Vue 2 compat: Has a tag (element or component)
        if (vnode.tag) return true;
        // Vue 3: Has a type that is not a Symbol (Fragment, Comment, Text are symbols)
        if (vnode.type && typeof vnode.type !== 'symbol') return true;
        // Vue 2 compat: Has non-empty text
        if (vnode.text && vnode.text.trim()) return true;
        // Vue 3: text node with string children
        if (typeof vnode.children === 'string' && vnode.children.trim()) return true;
        // Vue 2 compat / Vue 3: Has children array
        if (Array.isArray(vnode.children) && vnode.children.length > 0) return true;
        return false;
    });
};

/**
 * Splits a Vue 3 `$attrs` object into event listeners and non-event attributes.
 *
 * In Vue 3 (or compat mode with `INSTANCE_LISTENERS: false`), event listeners
 * are merged into `$attrs` with an `on` prefix (e.g. `onBlur`, `onKeydown`).
 * Components that use `inheritAttrs: false` and need to forward attrs and
 * listeners to different elements can use this to separate them.
 *
 * The returned `listeners` use unprefixed event names (e.g. `blur`, `keydown`)
 * so they can be used directly with `v-on`.
 *
 * Used by components that need to forward event listeners separately
 * from non-event attributes (e.g. to bind them on different elements).
 */
export const splitAttrs = (
    attrs: Record<string, unknown>
): { listeners: Record<string, unknown>; nonEventAttrs: Record<string, unknown> } => {
    const listeners: Record<string, unknown> = {};
    const nonEventAttrs: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(attrs)) {
        if (key.startsWith('on') && typeof value === 'function') {
            const eventName = key[2].toLowerCase() + key.slice(3);
            listeners[eventName] = value;
        } else {
            nonEventAttrs[key] = value;
        }
    }

    return { listeners, nonEventAttrs };
};

/**
 * Get the name of a component.
 * If the component has no name, it will return a random UUID.
 */
export const getComponentName = (component: Component): string => {
    const name =
        component.name ??
        // @ts-expect-error - the Vue internals might not be correctly typed
        component.__name ??
        // @ts-expect-error - the Vue internals might not be correctly typed
        component.__file ??
        // @ts-expect-error - the Vue internals might not be correctly typed
        component.options?.name ??
        // @ts-expect-error - the Vue internals might not be correctly typed
        component.extendOptions?.name;

    if (name !== undefined && name.length > 0) {
        return name;
    }

    return `AnonymousComponent-${uuidv4()}`;
};
